// 优化的数据API服务 - 优先从数据库获取数据
import api from './api'

// 数据API服务类
class DataApiService {
  constructor() {
    this.cache = new Map()
    this.cacheTimeout = 5 * 60 * 1000 // 5分钟缓存
  }

  // 检查缓存是否有效
  isCacheValid(key) {
    const cached = this.cache.get(key)
    return cached && (Date.now() - cached.timestamp) < this.cacheTimeout
  }

  // 设置缓存
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    })
  }

  // 获取缓存
  getCache(key) {
    const cached = this.cache.get(key)
    return cached ? cached.data : null
  }

  // 清除缓存
  clearCache(key = null) {
    if (key) {
      this.cache.delete(key)
    } else {
      this.cache.clear()
    }
  }

  // 获取JIRA数据（优先数据库）
  async getJiraIssues(force = false) {
    const cacheKey = 'jira_issues'
    
    // 检查本地缓存
    if (!force && this.isCacheValid(cacheKey)) {
      const cached = this.getCache(cacheKey)
      return {
        ...cached,
        fromLocalCache: true
      }
    }

    try {
      const response = await api.get('/data/jira', {
        params: { force }
      })
      
      const data = response.data
      
      // 缓存数据
      this.setCache(cacheKey, data)
      
      return {
        ...data,
        fromLocalCache: false
      }
    } catch (error) {
      console.error('Failed to fetch JIRA data:', error)
      
      // 如果请求失败，尝试返回缓存数据
      const cached = this.getCache(cacheKey)
      if (cached) {
        return {
          ...cached,
          fromLocalCache: true,
          error: error.userMessage || error.message
        }
      }
      
      throw error
    }
  }

  // 获取GitHub PRs（优先数据库）
  async getGitHubPRs(state = null, force = false) {
    const cacheKey = `github_prs_${state || 'all'}`
    
    // 检查本地缓存
    if (!force && this.isCacheValid(cacheKey)) {
      const cached = this.getCache(cacheKey)
      return {
        ...cached,
        fromLocalCache: true
      }
    }

    try {
      let url = '/data/github/prs'
      if (state === 'open') {
        url = '/data/github/prs/open'
      } else if (state === 'merged') {
        url = '/data/github/prs/merged'
      }

      const response = await api.get(url, {
        params: { force, state }
      })
      
      const data = response.data
      
      // 缓存数据
      this.setCache(cacheKey, data)
      
      return {
        ...data,
        fromLocalCache: false
      }
    } catch (error) {
      console.error('Failed to fetch GitHub PRs:', error)
      
      // 如果请求失败，尝试返回缓存数据
      const cached = this.getCache(cacheKey)
      if (cached) {
        return {
          ...cached,
          fromLocalCache: true,
          error: error.userMessage || error.message
        }
      }
      
      throw error
    }
  }

  // 获取开放的PRs
  async getOpenPRs(force = false) {
    return this.getGitHubPRs('open', force)
  }

  // 获取已合并的PRs
  async getMergedPRs(force = false) {
    return this.getGitHubPRs('merged', force)
  }

  // 获取成员数据（优先数据库）
  async getMembers(type = null, force = false) {
    const cacheKey = `members_${type || 'all'}`
    
    // 检查本地缓存
    if (!force && this.isCacheValid(cacheKey)) {
      const cached = this.getCache(cacheKey)
      return {
        ...cached,
        fromLocalCache: true
      }
    }

    try {
      const response = await api.get('/data/members', {
        params: { force, type }
      })
      
      const data = response.data
      
      // 缓存数据
      this.setCache(cacheKey, data)
      
      return {
        ...data,
        fromLocalCache: false
      }
    } catch (error) {
      console.error('Failed to fetch members:', error)
      
      // 如果请求失败，尝试返回缓存数据
      const cached = this.getCache(cacheKey)
      if (cached) {
        return {
          ...cached,
          fromLocalCache: true,
          error: error.userMessage || error.message
        }
      }
      
      throw error
    }
  }

  // 获取PMC成员
  async getPMCMembers(force = false) {
    return this.getMembers('pmc', force)
  }

  // 获取自定义成员
  async getCustomMembers(force = false) {
    return this.getMembers('custom', force)
  }

  // 获取数据统计
  async getStats() {
    try {
      const response = await api.get('/data/stats')
      return response.data
    } catch (error) {
      console.error('Failed to fetch stats:', error)
      throw error
    }
  }

  // 手动触发数据同步
  async triggerSync(type = 'all') {
    try {
      const response = await api.post('/sync/trigger', null, {
        params: { type }
      })
      
      // 清除相关缓存
      if (type === 'all') {
        this.clearCache()
      } else {
        this.clearCache(`${type}_data`)
      }
      
      return response.data
    } catch (error) {
      console.error('Failed to trigger sync:', error)
      throw error
    }
  }

  // 获取同步状态
  async getSyncStatus() {
    try {
      const response = await api.get('/sync/trigger')
      return response.data
    } catch (error) {
      console.error('Failed to get sync status:', error)
      throw error
    }
  }

  // 清除服务器缓存
  async clearServerCache() {
    try {
      const response = await api.post('/data/clear-cache')
      return response.data
    } catch (error) {
      console.error('Failed to clear server cache:', error)
      throw error
    }
  }

  // 格式化时间显示
  formatTime(timeString) {
    if (!timeString) return '未知'
    
    const time = new Date(timeString)
    const now = new Date()
    const diff = now - time
    
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (minutes < 1) {
      return '刚刚'
    } else if (minutes < 60) {
      return `${minutes}分钟前`
    } else if (hours < 24) {
      return `${hours}小时前`
    } else if (days < 7) {
      return `${days}天前`
    } else {
      return time.toLocaleDateString('zh-CN')
    }
  }

  // 格式化数据源显示
  formatDataSource(source, cached = false) {
    const sourceMap = {
      'database': '数据库',
      'memory': '内存缓存',
      'fallback': '备用源',
      'error': '错误'
    }
    
    let sourceText = sourceMap[source] || source
    
    if (cached) {
      sourceText += ' (缓存)'
    }
    
    return sourceText
  }
}

// 创建单例实例
const dataApi = new DataApiService()

export default dataApi
